import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Grid, Card, CardContent, Typography, CircularProgress,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Divider, Stack, Avatar, TablePagination, IconButton, Tooltip, Button, Tabs, Tab
} from '@mui/material';
import { AccountCircle, BusinessCenter, Assessment, EmojiEvents, MonetizationOn, Handshake, FilterList, CommentOutlined } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getOpportunityDashboard } from '../../../Redux/actions';
import Filter from '../../Common/Filter';
import { updateParams } from '../../../utils';
import { datePickerSlotProps, tabStyle, tabListStyle } from '../../../theme/formStyles';
import LeadTab from '../../Opportunity/Lead/LeadTab';

const formatCurrencyShort = (value) => {
    if (!value || isNaN(value)) return '₹0';
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value.toFixed(0)}`;
};

const getCurrentFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed (0 = January)
    
    // Financial year starts in April (month 3)
    if (currentMonth >= 3) {
        // April to December: FY is current year to next year
        return { start: currentYear, end: currentYear + 1, label: `FY${String(currentYear + 1).slice(-2)}` };
    } else {
        // January to March: FY is previous year to current year
        return { start: currentYear - 1, end: currentYear, label: `FY${String(currentYear).slice(-2)}` };
    }
};

const getStatusStyle = (status) => {
    switch (status) {
      case 'Qualified Lead':
      case 'Won':
        return { color: '#196a3e', bgcolor: '#f1faf4', border: '1px solid #c2e9cf' };
      case 'Feasibility Study':
        return { color: '#1a6894', bgcolor: '#f1f8fc', border: '1px solid #b8ddec' };
      case 'Proposal':
        return { color: '#8f6d1d', bgcolor: '#fffdee', border: '1px solid #eed699' };
      case 'Negotiation':
        return { color: '#a44d21', bgcolor: '#fff6ef', border: '1px solid #f4c1a5' };
      case 'Lost':
        return { color: '#c62828', bgcolor: '#ffebee', border: '1px solid #ffcdd2' };
      case 'Hold':
        return { color: '#5c5c00', bgcolor: '#fffde7', border: '1px solid #fff176' };
      default:
        return { color: '#757575', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' };
    }
};

const getSalespersonStyle = (name) => {
    if (!name || name === 'Unassigned') return { color: '#757575', bgcolor: '#f5f5f5' };
    const hash = Array.from(name).reduce((acc, char) => acc + char.charCodeAt(0), 0) % 4;
    return [
        { color: '#196a3e', bgcolor: '#f1faf4' },
        { color: '#1a6894', bgcolor: '#f1f8fc' },
        { color: '#a44d21', bgcolor: '#fff6ef' },
        { color: '#880e4f', bgcolor: '#fce4ec' }
    ][hash];
};

const getRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const diff = new Date() - new Date(dateString);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days/7)}w ago`;
    return `${Math.floor(days/30)}mo ago`;
};

const formatStageComments = (opp) => {
    const comments = [];

    if (opp.qualified_lead_comment) {
        comments.push({ stage: 'Qualified Lead', comment: opp.qualified_lead_comment });
    }
    if (opp.feasibility_comment) {
        comments.push({ stage: 'Feasibility Study', comment: opp.feasibility_comment });
    }
    if (opp.proposals && opp.proposals.length > 0) {
        const sortedProposals = [...opp.proposals].sort((a, b) => a.id - b.id);
        sortedProposals.forEach((proposal, idx) => {
            if (proposal.comment || proposal.mode_of_communication) {
                const proposalLabel = sortedProposals.length > 1 ? `Proposal ${idx + 1}` : 'Proposal';
                comments.push({ stage: proposalLabel, comment: proposal.comment, modeOfCommunication: proposal.mode_of_communication });
            }
        });
    }
    if (opp.negotiations && opp.negotiations.length > 0) {
        const sortedNegotiations = [...opp.negotiations].sort((a, b) => a.id - b.id);
        sortedNegotiations.forEach((neg, idx) => {
            if (neg.comment || neg.negotiation_mode_of_communication) {
                const negLabel = sortedNegotiations.length > 1 ? `Negotiation ${idx + 1}` : 'Negotiation';
                comments.push({ stage: negLabel, comment: neg.comment, modeOfCommunication: neg.negotiation_mode_of_communication });
            }
        });
    }
    if (opp.won_lost_comment) {
        comments.push({ stage: 'Won/Lost', comment: opp.won_lost_comment });
    }

    return comments;
};

const KPICard = ({ title, value, subtitle, icon, gradient }) => (
    <Card sx={{ 
        height: '100%', 
        background: gradient,
        color: '#fff',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
    }}>
        <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.2 }}>
            {icon && React.cloneElement(icon, { sx: { fontSize: 140 } })}
        </Box>
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1, opacity: 0.9 }}>
                {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {value}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.85 }}>
                {subtitle}
            </Typography>
        </CardContent>
    </Card>
);

function QualifiedLeadPanel() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [bdPage, setBdPage] = useState(0);
    const [bdRowsPerPage, setBdRowsPerPage] = useState(10);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [bdFilterOptions, setBdFilterOptions] = useState({});
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [pendingFromDate, setPendingFromDate] = useState(null);
    const [pendingToDate, setPendingToDate] = useState(null);

    useEffect(() => {
        setPage(0);
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams]);

    useEffect(() => {
        fetchData();
    // Refetch whenever the tab becomes visible (e.g. after navigating back from edit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = () => {
        setLoading(true);
        const existingParams = Object.fromEntries([...queryParams]);
        const params = {
            salesperson: existingParams?.salesperson || '',
            client: existingParams?.client || '',
            location: existingParams?.location || '',
            stage: existingParams?.stage || '',
        };
        dispatch(getOpportunityDashboard(params)).then((res) => {
            if (res?.data?.success) {
                setData(res.data.data);
                if (res.data.data?.filterOptions) {
                    setBdFilterOptions(res.data.data.filterOptions);
                }
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    const updateQueryParams = (params) => {
        const existingParams = Object.fromEntries([...queryParams]);
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleBdChangePage = (event, newPage) => {
        setBdPage(newPage);
    };

    const handleBdChangeRowsPerPage = (event) => {
        setBdRowsPerPage(parseInt(event.target.value, 10));
        setBdPage(0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#008080' }} />
            </Box>
        );
    }

    if (!data) return null;

    const existingParams = Object.fromEntries([...queryParams]);
    const selectedSalesperson = existingParams?.salesperson || '';

    const currentFY = getCurrentFinancialYear();
    const fyStartDate = new Date(currentFY.start, 3, 1);
    const fyEndDate = new Date(currentFY.end, 2, 31, 23, 59, 59);

    const allOpportunities = [];
    if (data.clientWise) {
        data.clientWise.forEach(client => {
            if (client.opportunities) {
                client.opportunities.forEach(opp => {
                    allOpportunities.push({ ...opp, client_name: client.client_name });
                });
            }
        });
    }
    allOpportunities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const getStageDate = (opp) => {
        switch (opp.status) {
            case 'Qualified Lead':
                return opp.qualified_lead_date;
            case 'Feasibility Study':
                return opp.feasibility_date;
            case 'Proposal': {
                const sorted = opp.proposals?.length > 0 ? [...opp.proposals].sort((a, b) => b.id - a.id) : [];
                return sorted[0]?.proposal_date || sorted[0]?.created_at || opp.qualified_lead_date;
            }
            case 'Negotiation': {
                const sorted = opp.negotiations?.length > 0 ? [...opp.negotiations].sort((a, b) => b.id - a.id) : [];
                return sorted[0]?.negotiation_date || sorted[0]?.created_at || opp.qualified_lead_date;
            }
            case 'Won':
            case 'Lost':
                return opp.won_lost_date;
            default:
                return opp.updated_at;
        }
    };

    const filteredOpportunities = allOpportunities.filter(opp => {
        if (!fromDate && !toDate) return true;
        const stageDate = getStageDate(opp);
        if (!stageDate) return true;
        const d = dayjs(stageDate).startOf('day');
        if (fromDate && d.isBefore(dayjs(fromDate).startOf('day'))) return false;
        if (toDate && d.isAfter(dayjs(toDate).startOf('day'))) return false;
        return true;
    });

    // Recompute all KPIs from filtered set
    let wonCurrentFY = 0;
    let wonCountCurrentFY = 0;
    let totalPipeline = 0;
    let activeOpportunityCount = 0;
    let inNegotiation = 0;

    filteredOpportunities.forEach(opp => {
        const pv = opp.pipeline_value || 0;
        if (opp.status !== 'Won' && opp.status !== 'Lost') {
            totalPipeline += pv;
            activeOpportunityCount++;
        }
        if (opp.status === 'Negotiation') inNegotiation += pv;
        if (opp.status === 'Won' && opp.won_lost_date) {
            const wonDate = new Date(opp.won_lost_date);
            if (wonDate >= fyStartDate && wonDate <= fyEndDate) {
                wonCurrentFY += Number(opp.po_value_ex_gst) || 0;
                wonCountCurrentFY++;
            }
        }
    });

    const avgDealSize = filteredOpportunities.length > 0
        ? filteredOpportunities.reduce((sum, opp) => sum + (opp.pipeline_value || 0), 0) / filteredOpportunities.length
        : 0;

    const ALL_STAGES = ['Qualified Lead', 'Feasibility Study', 'Proposal', 'Negotiation', 'Won', 'Lost', 'Hold'];
    const rawStageCounts = filteredOpportunities.reduce((acc, opp) => {
        acc[opp.status] = (acc[opp.status] || 0) + 1;
        return acc;
    }, {});
    const filteredStageCounts = ALL_STAGES.reduce((acc, stage) => {
        acc[stage] = rawStageCounts[stage] || 0;
        return acc;
    }, {});

    const activeBDs = {};
    filteredOpportunities.forEach(opp => {
        if (opp.bd_owners && opp.bd_owners.length > 0) {
            opp.bd_owners.forEach(bd => {
                const name = bd.name;
                if (!activeBDs[name]) {
                    activeBDs[name] = { name: name, pipelineValue: 0, deals: 0, wonDeals: 0 };
                }
                activeBDs[name].pipelineValue += opp.pipeline_value || 0;
                activeBDs[name].deals += 1;
                if (opp.status === 'Won') activeBDs[name].wonDeals += 1;
            });
        }
    });

    const bdCards = Object.values(activeBDs)
        .sort((a, b) => b.pipelineValue - a.pipelineValue)
        .map((bd) => ({
            ...bd,
            winRate: bd.deals > 0 ? Math.round((bd.wonDeals / bd.deals) * 100) : 0,
            targetPercentage: Math.max(10, Math.min(100, Math.round((bd.pipelineValue / (totalPipeline || 1)) * 100 * 2)))
        }));

    return (
        <Box sx={{ p: 2 }}>
            {/* Top bar: date filters + filter button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="From Date"
                        value={pendingFromDate}
                        onChange={(val) => setPendingFromDate(val)}
                        format="DD/MM/YYYY"
                        slotProps={datePickerSlotProps({ size: 'small' })}
                    />
                    <DatePicker
                        label="To Date"
                        value={pendingToDate}
                        onChange={(val) => setPendingToDate(val)}
                        format="DD/MM/YYYY"
                        slotProps={datePickerSlotProps({ size: 'small' })}
                    />
                </LocalizationProvider>
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => { setFromDate(pendingFromDate); setToDate(pendingToDate); setPage(0); }}
                    sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' }, textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                    Apply
                </Button>
                {(fromDate || toDate) && (
                    <Button size="small" variant="outlined" onClick={() => { setPendingFromDate(null); setPendingToDate(null); setFromDate(null); setToDate(null); setPage(0); }}
                        sx={{ borderColor: '#008080', color: '#008080', textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Clear
                    </Button>
                )}
                <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                    <FilterList />
                </IconButton>
            </Box>

            <Filter
                filterArea="Opportunity"
                showFilterDialog={showFilterDialog}
                setShowFilterDialog={setShowFilterDialog}
                updateQueryParams={updateQueryParams}
                queryParams={queryParams}
                setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                bdFilterOptions={bdFilterOptions}
            />

            {/* KPI Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Total Active Pipeline"
                        value={formatCurrencyShort(totalPipeline)}
                        subtitle={`${activeOpportunityCount} opportunities`}
                        icon={<Assessment />}
                        gradient="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={`Won (${currentFY.label})`}
                        value={formatCurrencyShort(wonCurrentFY)}
                        subtitle={`${wonCountCurrentFY} projects closed`}
                        icon={<EmojiEvents />}
                        gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="In Negotiation"
                        value={formatCurrencyShort(inNegotiation)}
                        subtitle={`${filteredStageCounts['Negotiation'] || 0} opportunities`}
                        icon={<Handshake />}
                        gradient="linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Avg Deal Size"
                        value={formatCurrencyShort(avgDealSize)}
                        subtitle="across all stages"
                        icon={<MonetizationOn />}
                        gradient="linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)"
                    />
                </Grid>
            </Grid>

            {/* Unified Bottom Section */}
            <Grid container spacing={4}>
                {/* Left Side Unified Column */}
                <Grid item xs={12} lg={8}>
                    {/* Stage Numbers Row */}
                    <Paper sx={{ px: 2, py: 1.5, mb: 4, borderRadius: 2, boxShadow: 'none', border: '1px solid #eaeaea' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#555', letterSpacing: 0.5 }}>PIPELINE STAGES</Typography>
                        <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 0.5, width: '100%' }}>
                            {Object.entries(filteredStageCounts).map(([stage, count]) => (
                                <Card key={stage} sx={{ flex: 1, minWidth: 100, ...getStatusStyle(stage), px: 1.5, py: 1, borderRadius: 2, boxShadow: 'none' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', textTransform: 'uppercase' }}>{stage}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{count}</Typography>
                                </Card>
                            ))}
                        </Stack>
                    </Paper>

                    {/* Client Wise - Unified Table */}
                    <Paper sx={{ p: 0, borderRadius: 2, boxShadow: 'none', display: 'flex', flexDirection: 'column', border: '1px solid #eaeaea' }}>
                        <TableContainer sx={{ flexGrow: 1 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>CLIENT / PROJECT</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>LOCATION</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>SALESPERSON</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>STAGE</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>VALUE</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>QUALIFIED DATE</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>PIPELINE DAYS</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>LAST UPDATED</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOpportunities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((opp, index) => {
                                        // Get salespersons to display
                                        let salespersons = [];
                                        if (opp.bd_owners && opp.bd_owners.length > 0) {
                                            if (selectedSalesperson) {
                                                // If filter is active, show only the filtered salesperson
                                                const filteredOwner = opp.bd_owners.find(bd => bd.name === selectedSalesperson);
                                                salespersons = filteredOwner ? [filteredOwner.name] : [opp.bd_owners[0].name];
                                            } else {
                                                // No filter, show all bd_owners
                                                salespersons = opp.bd_owners.map(bd => bd.name);
                                            }
                                        } else {
                                            salespersons = ['Unassigned'];
                                        }
                                        
                                        return (
                                            <TableRow key={index} sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.5 } }}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#222' }}>{opp.client_name}</Typography>
                                                    <Typography variant="caption" sx={{ color: '#666' }}>{opp.opportunity_name}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: '#555', fontSize: '0.85rem' }}>
                                                        {opp.location?.name || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {salespersons.map((spoc, spIdx) => {
                                                            const spStyle = getSalespersonStyle(spoc);
                                                            return (
                                                                <Box key={spIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: spStyle.bgcolor, color: spStyle.color, px: 1.2, py: 0.3, borderRadius: 4, fontFamily: 'sans-serif', width: 'fit-content' }}>
                                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: spStyle.color, mr: 0.8 }} />
                                                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{spoc}</Typography>
                                                                    </Box>
                                                                    {spIdx === 0 && formatStageComments(opp).length > 0 && (
                                                                        <Tooltip 
                                                                            title={
                                                                                <Box sx={{ p: 1 }}>
                                                                                    {formatStageComments(opp).map((item, idx) => (
                                                                                        <Box key={idx} sx={{ mb: idx < formatStageComments(opp).length - 1 ? 1.5 : 0 }}>
                                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#ffd700', display: 'block', mb: 0.5 }}>
                                                                                                {item.stage}
                                                                                            </Typography>
                                                                                            {item.comment && (
                                                                                                <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                                                                                                    {item.comment}
                                                                                                </Typography>
                                                                                            )}
                                                                                            {item.modeOfCommunication && (
                                                                                                <Box component="span" sx={{ display: 'inline-block', mt: 0.5, ml: item.comment ? 0.5 : 0, bgcolor: 'rgba(144,202,249,0.15)', color: '#90caf9', border: '1px solid rgba(144,202,249,0.4)', borderRadius: '10px', px: 0.8, py: 0.1, fontSize: '0.65rem', fontWeight: 600, verticalAlign: 'middle' }}>
                                                                                                    {item.modeOfCommunication}
                                                                                                </Box>
                                                                                            )}
                                                                                        </Box>
                                                                                    ))}
                                                                                </Box>
                                                                            }
                                                                            arrow
                                                                            placement="right"
                                                                        >
                                                                            <CommentOutlined sx={{ fontSize: '1rem', color: '#666', cursor: 'pointer', '&:hover': { color: '#008080' } }} />
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={opp.status} 
                                                        size="small" 
                                                        sx={{ fontWeight: 500, minWidth: '70px', ...getStatusStyle(opp.status) }} 
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#333' }}>
                                                    {formatCurrencyShort(opp.pipeline_value)}
                                                </TableCell>
                                                <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                                                    {opp.qualified_lead_date ? new Date(opp.qualified_lead_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                                </TableCell>
                                                <TableCell sx={{ color: '#555', fontSize: '0.85rem', fontWeight: 600 }}>
                                                    {opp.qualified_lead_date ? Math.floor((new Date() - new Date(opp.qualified_lead_date)) / (1000 * 60 * 60 * 24)) + ' days' : '-'}
                                                </TableCell>
                                                <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                                                    {getRelativeTime(opp.updated_at)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredOpportunities.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#888' }}>
                                                No opportunities found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {filteredOpportunities.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={filteredOpportunities.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Row Items:"
                            sx={{ 
                                borderTop: '1px solid #eaeaea',
                                '.MuiTablePagination-spacer': {
                                    display: 'none'
                                },
                                '.MuiInputBase-root': {
                                    marginRight: 'auto'
                                },
                                '.MuiSelect-select': { minWidth: '15px !important', width: '15px' },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    marginTop: '0px !important',
                                    marginBottom: '0px !important'
                                }
                            }}
                        />
                        )}
                    </Paper>
                </Grid>

                {/* BD Owner Ranking - Right Side */}
                <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 0, borderRadius: 2, boxShadow: 'none', height: '100%', bgcolor: 'transparent' }}>
                        <Box sx={{ pb: 1, pt: 1, pl: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#555', letterSpacing: 0.5 }}>PIPELINE VALUE RANKING</Typography>
                        </Box>
                        <Stack spacing={1} sx={{ flexGrow: 1, px: 0 }}>
                            {bdCards.slice(bdPage * bdRowsPerPage, bdPage * bdRowsPerPage + bdRowsPerPage).map((bd, index) => {
                                const spStyle = getSalespersonStyle(bd.name);
                                const parts = bd.name.trim().split(' ');
                                const initials = parts.length > 1 ? (parts[0][0] + parts[1][0]).toUpperCase() : bd.name.substring(0,2).toUpperCase();
                                const trueIndex = (bdPage * bdRowsPerPage) + index + 1;
                                
                                const isLast = index === bdCards.slice(bdPage * bdRowsPerPage, bdPage * bdRowsPerPage + bdRowsPerPage).length - 1;
                                return (
                                    <Paper key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 3, border: '1px solid #eaeaea', borderBottom: isLast ? 'none' : '1px solid #eaeaea', boxShadow: 'none' }}>
                                        {/* Rank */}
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: spStyle.color, width: 30, textAlign: 'center' }}>
                                            {trueIndex}
                                        </Typography>
                                        
                                        {/* Avatar */}
                                        <Avatar sx={{ width: 40, height: 40, bgcolor: spStyle.bgcolor, color: spStyle.color, fontSize: '0.9rem', fontWeight: 600, mx: 2 }}>
                                            {initials}
                                        </Avatar>
                                        
                                        {/* Info */}
                                        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bd.name}</Typography>
                                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {bd.deals} deals &middot; {bd.winRate}% win rate
                                            </Typography>
                                        </Box>

                                        {/* Capsule Bar */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, flexShrink: 0 }}>
                                            <Box sx={{ width: 40, height: 4, bgcolor: spStyle.color, borderRadius: 2 }} />
                                        </Box>

                                        {/* Value & Target */}
                                        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 70 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#222', whiteSpace: 'nowrap' }}>
                                                {formatCurrencyShort(bd.pipelineValue)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                Target: {bd.targetPercentage}%
                                            </Typography>
                                        </Box>
                                    </Paper>
                                );
                            })}
                            {bdCards.length === 0 && (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" color="textSecondary">No data available.</Typography>
                                </Box>
                            )}
                        </Stack>
                        {bdCards.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30]}
                            component="div"
                            count={bdCards.length}
                            rowsPerPage={bdRowsPerPage}
                            page={bdPage}
                            onPageChange={handleBdChangePage}
                            onRowsPerPageChange={handleBdChangeRowsPerPage}
                            labelRowsPerPage="Row Items:"
                            sx={{ 
                                '.MuiTablePagination-spacer': { display: 'none' },
                                '.MuiInputBase-root': { marginRight: 'auto', width: 'auto' },
                                '.MuiSelect-select': { minWidth: '15px !important', width: '15px' },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    marginTop: '0px !important',
                                    marginBottom: '0px !important'
                                },
                                'MuiTablePagination-root':{ marginTop: '0px' }
                            }}
                        />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default function Opportunity() {
    const params = useParams();
    const navigate = useNavigate();
    const subView = params['*'] || '';
    const [activeTab, setActiveTab] = useState('lead');

    // Canonicalize the bare /business-dashboard/opportunity URL to the Lead list route on first load.
    useEffect(() => {
        if (!subView) {
            navigate('/business-dashboard/opportunity/lead?page=1', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTopTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'lead') {
            navigate('/business-dashboard/opportunity/lead?page=1');
        } else {
            navigate('/business-dashboard/opportunity');
        }
    };

    return (
        <Box>
            <Box sx={{ pt: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(e, val) => handleTopTabChange(val)} sx={tabListStyle}>
                    <Tab label="Lead" value="lead" sx={tabStyle} />
                    <Tab label="Qualified Lead" value="qualified" sx={tabStyle} />
                </Tabs>
            </Box>

            {activeTab === 'lead' ? <LeadTab subView={subView} /> : <QualifiedLeadPanel />}
        </Box>
    );
}
